import { useEffect, useState } from "react";
import { utilityAnimationDurationMs } from "../../../shared/lib/trajectory-animation.js";
import type { PlaybackStep } from "./playback.js";

/**
 * Schritt-Wiedergabe der Quickview.
 *
 * Die Zustandsmaschine hat drei ineinandergreifende Größen, die man einzeln
 * nicht versteht:
 *
 * - `selectedStepOrder` — der sichtbar hervorgehobene Schritt.
 * - `walkingStepOrder` — der Schritt, auf den der Spieler gerade **zuläuft**.
 *   Er wird vor dem Wechsel gesetzt, damit die Figur unterwegs ist, bevor die
 *   Auswahl umspringt; ohne das wirkt die Wiedergabe wie ein Sprung.
 * - `utilityStepOrder` — ein Schritt, dessen Wurf gerade animiert wird. Er
 *   hält die Kette an, bis die Flugbahn durch ist.
 *
 * Bis Phase 11 lag das als 45-Zeilen-Effekt in `QuickviewPage.tsx`, zusammen
 * mit acht weiteren Effekten. Hier ist es für sich lesbar und ohne die Seite
 * benutzbar.
 */
export function usePlayback(options: {
  plan: readonly PlaybackStep[];
  animatedUtilitySteps: ReadonlySet<number>;
  /** Wechselt die Taktik oder der Spieler, hält die Wiedergabe an. */
  resetKey: string;
}) {
  const [selectedStepOrder, setSelectedStepOrder] = useState<number | null>(
    null,
  );
  const [walkingStepOrder, setWalkingStepOrder] = useState<number | null>(null);
  const [utilityStepOrder, setUtilityStepOrder] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);

  const { plan, animatedUtilitySteps, resetKey } = options;

  useEffect(() => {
    if (!playing || !plan.length) {
      setWalkingStepOrder(null);
      setUtilityStepOrder(null);
      if (playing) setPlaying(false);
      return;
    }
    // Läuft gerade ein Wurf, wartet die Kette auf dessen Ende.
    if (utilityStepOrder !== null && utilityStepOrder === selectedStepOrder) {
      const timer = setTimeout(
        () => setUtilityStepOrder(null),
        utilityAnimationDurationMs,
      );
      return () => clearTimeout(timer);
    }
    const index = plan.findIndex((step) => step.order === selectedStepOrder);
    if (index < 0) {
      // Wiedergabe ohne gültige Auswahl beginnt vorne.
      setWalkingStepOrder(null);
      setUtilityStepOrder(null);
      setSelectedStepOrder(plan[0]!.order);
      return;
    }
    const next = plan[index + 1];
    setWalkingStepOrder(next?.order ?? null);
    setUtilityStepOrder(null);
    const timer = setTimeout(() => {
      if (!next) {
        setWalkingStepOrder(null);
        setUtilityStepOrder(null);
        setPlaying(false);
        return;
      }
      setSelectedStepOrder(next.order);
      setWalkingStepOrder(null);
      setUtilityStepOrder(
        animatedUtilitySteps.has(next.order) ? next.order : null,
      );
    }, plan[index]!.durationMs);
    return () => clearTimeout(timer);
  }, [
    animatedUtilitySteps,
    plan,
    playing,
    selectedStepOrder,
    utilityStepOrder,
  ]);

  // Ein Taktik- oder Spielerwechsel darf keine laufende Wiedergabe mitschleppen.
  useEffect(() => {
    setPlaying(false);
    setWalkingStepOrder(null);
    setUtilityStepOrder(null);
  }, [resetKey]);

  return {
    selectedStepOrder,
    setSelectedStepOrder,
    walkingStepOrder,
    utilityStepOrder,
    playing,
    setPlaying,
  };
}
